import { useEffect, useState } from "react";
import { Play, Pause, Square, Loader2, Plus, Minus, ChevronDown, LayoutGrid, VolumeX, Volume2, Settings, ListMusic, Layers, Folder, FileAudio, Radio, Rewind, FastForward, SkipBack, SkipForward, Repeat, Shuffle } from "lucide-react";
import { DeckVisualizer, DECK_MODES } from "@/components/listen/DeckVisualizer";
import { EqPanel } from "@/components/listen/EqPanel";

const ptime = (s) => {
  const t = Math.max(0, Math.floor(s || 0));
  return `${String(Math.floor(t / 60)).padStart(2, "0")}'${String(t % 60).padStart(2, "0")}"`;
};

const useClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 10000); return () => clearInterval(t); }, []);
  return now;
};

const SideBtn = ({ children, onClick, label, testid, className = "" }) => (
  <button onClick={onClick} aria-label={label} className={`pio-side-btn ${className}`} data-testid={testid}>{children}</button>
);

const SideRail = ({ vol, audio, next }) => (
  <div className="pio-rail" data-testid="pioneer-side-rail">
    <span className="pio-mic-label">MIC</span>
    <SideBtn onClick={() => vol.setVolume(vol.volume + 5)} label="Volume up" testid="pioneer-vol-up"><Plus className="w-4 h-4" /></SideBtn>
    <SideBtn onClick={() => vol.setVolume(vol.volume - 5)} label="Volume down" testid="pioneer-vol-down"><Minus className="w-4 h-4" /></SideBtn>
    <span className="pio-dots" aria-hidden />
    <SideBtn onClick={audio.toggle} label="Play or stop" testid="pioneer-home-button" className="pio-home"><LayoutGrid className="w-5 h-5" /></SideBtn>
    <span className="pio-dots" aria-hidden />
    <SideBtn onClick={vol.toggleMute} label="Mute" testid="pioneer-mute-button">{vol.volume ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}</SideBtn>
    <SideBtn onClick={next} label="Next visualizer" testid="pioneer-rail-next" className="mt-auto"><ChevronDown className="w-5 h-5" /></SideBtn>
  </div>
);

const Chip = ({ children, on, onClick, testid, label }) => (
  <button onClick={onClick} aria-label={label} className={`pio-chip ${on ? "on" : ""}`} data-testid={testid}>{children}</button>
);

export const PioneerDeck = ({ status, elapsed, audio, clock, vol }) => {
  const np = status?.now_playing;
  const now = useClock();
  const [mode, setMode] = useState(() => localStorage.getItem("deck-mode") || "AUTO");
  const [autoShown, setAutoShown] = useState("SPEC");
  const [panel, setPanel] = useState(null); // null | "eq" | "modes"
  useEffect(() => { localStorage.setItem("deck-mode", mode); }, [mode]);
  const shift = (d) => setMode(DECK_MODES[(DECK_MODES.indexOf(mode) + d + DECK_MODES.length) % DECK_MODES.length]);
  const pct = np?.duration ? Math.min(100, (elapsed / np.duration) * 100) : 0;
  const bpm = Math.round(np?.bpm_now || np?.bpm || 0);
  const shown = mode === "AUTO" ? autoShown : mode;
  const quick = ["SPEC", "LOWRIDER", "CITY", "FIRE"];
  const toggle = (p) => setPanel(panel === p ? null : p);

  return (
    <section className="pio-unit max-w-4xl mx-auto w-full rise" data-testid="public-player-card" data-player="pioneer">
      <div className="pio-bezel">
        <SideRail vol={vol} audio={audio} next={() => shift(1)} />
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="pio-top">
            <span className="pio-brand" data-testid="deck-brand">{(status?.station?.name || "Lowride Life").replace(/radio/i, "").trim()}</span>
            <span className="pio-badges"><Radio className="w-3 h-3" /> 128K HLS</span>
          </div>
          <div className="pio-screen" data-testid="pioneer-screen">
            <DeckVisualizer clock={clock} audioApi={audio} playing={audio.playing} mode={mode} onAutoMode={(m) => m !== autoShown && setAutoShown(m)}
              className="absolute inset-0 w-full h-full" />
            <div className="pio-shade" aria-hidden />
            <div className="relative z-10 flex flex-col h-full p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <button className="pio-drop" onClick={() => toggle("modes")} aria-label="Visualizer list" data-testid="pioneer-mode-list-button"><ChevronDown className="w-4 h-4" /></button>
                <div className="flex flex-col gap-1 pt-1" data-testid="deck-mode-label-wrap">
                  <span className="font-mono text-[0.6rem] tracking-[0.2em] text-white/80" data-testid="deck-mode-label">{mode === "AUTO" ? `AUTO·${shown}` : shown}</span>
                  <span className="pio-tag hidden sm:inline-block w-fit">Lowride Audio · {status?.live ? "LIVE MIC" : "AUTO DJ"}</span>
                </div>
                <div className="ml-auto flex items-baseline gap-2">
                  {status?.live && <span className="pio-live on-air-pulse" data-testid="public-player-live-badge">LIVE</span>}
                  <span className="font-mono text-[0.6rem] text-white/70" data-testid="console-bpm">{bpm ? `${bpm} BPM` : "--- BPM"}</span>
                  <span className="pio-clock" data-testid="pioneer-clock">{now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).replace(/\s?[AP]M/i, "")}</span>
                </div>
              </div>
              <div className="flex-1 flex min-h-0">
                <div className="flex-1 min-w-0 flex flex-col justify-center gap-1.5 sm:gap-2 pr-2">
                  <p className="pio-line" data-testid="public-player-now-playing-artist"><Folder className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" /><span className="truncate">{np?.artist || status?.station?.tagline || "Lowride Life Radio"}</span></p>
                  <p className="pio-line pio-title" data-testid="public-player-now-playing-title"><FileAudio className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" /><span className="truncate">{np?.title || (status ? "Station idle" : "Tuning in...")}</span></p>
                  <p className="font-mono text-[0.6rem] text-white/70 flex gap-3" data-testid="station-onair-status">
                    <span>{np ? "ON AIR" : "OFF AIR"}</span><span data-testid="station-listener-count"><b>{status?.listeners ?? 0}</b> LISTENING</span>
                  </p>
                </div>
                <div className="flex flex-col items-center justify-between py-1 gap-2">
                  <button className={`pio-icon ${panel === "modes" ? "on" : ""}`} onClick={() => toggle("modes")} aria-label="Visualizer settings" data-testid="pioneer-settings-button"><Settings className="w-6 h-6" /></button>
                  <button className={`pio-icon pio-eq-text ${panel === "eq" ? "on" : ""}`} onClick={() => toggle("eq")} data-testid="pioneer-eq-button">EQ</button>
                  <button className="pio-icon" onClick={() => shift(1)} aria-label="Next visualizer" data-testid="pioneer-list-button"><ListMusic className="w-6 h-6" /></button>
                  <button className={`pio-icon ${mode === "AUTO" ? "on" : ""}`} onClick={() => setMode("AUTO")} aria-label="Auto visualizer" data-testid="pioneer-auto-icon"><Layers className="w-5 h-5" /></button>
                </div>
              </div>
              <div className="flex items-center justify-between font-mono text-[0.7rem] sm:text-sm text-white">
                <span data-testid="pioneer-elapsed">{ptime(np ? elapsed : 0)}</span>
                <span className="font-mono text-[0.6rem] text-white/60">{status?.live ? "LIVE MIC" : "AUTO DJ"}</span>
                <span data-testid="pioneer-remaining">-{ptime(np?.duration ? np.duration - elapsed : 0)}</span>
              </div>
              <div className="pio-progress" data-testid="console-tuner"><div style={{ width: `${pct}%` }} data-testid="public-player-progress" /><i style={{ left: `${pct}%` }} /></div>
              <div className="pio-row" data-testid="deck-presets">
                {quick.map((m) => <Chip key={m} on={mode === m} onClick={() => setMode(m)} testid={`deck-preset-${m.toLowerCase()}`}>{m}</Chip>)}
                <Chip on={false} onClick={() => shift(-1)} testid="pioneer-prev-mode" label="Previous visualizer"><SkipBack className="w-4 h-4" /></Chip>
                <Chip on={false} onClick={() => shift(1)} testid="pioneer-next-mode" label="Next visualizer"><SkipForward className="w-4 h-4" /></Chip>
              </div>
              {panel && (
                <div className="pio-overlay" data-testid={`pioneer-${panel}-overlay`}>
                  {panel === "eq" ? <EqPanel audio={audio} accent="#3ee07a" /> : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 pt-7" data-testid="pioneer-mode-grid">
                      {DECK_MODES.map((m) => <Chip key={m} on={mode === m} onClick={() => { setMode(m); setPanel(null); }} testid={`pioneer-mode-${m.toLowerCase()}`}>{m}</Chip>)}
                    </div>
                  )}
                  <button className="pio-close" onClick={() => setPanel(null)} data-testid="pioneer-overlay-close">CLOSE</button>
                </div>
              )}
            </div>
          </div>
          <div className="pio-transport">
            <button onClick={audio.toggle} disabled={audio.loading} className="pio-play" aria-label={audio.playing ? "Pause" : "Play"}
              data-testid={audio.playing ? "console-pause-button" : "console-play-button"}>
              {audio.loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Play className="w-5 h-5 fill-current" /><span className="text-lg leading-none">/</span><Pause className="w-5 h-5 fill-current" /></>}
            </button>
            <button onClick={() => shift(-1)} className="pio-t" aria-label="Previous visualizer" data-testid="pioneer-rewind"><Rewind className="w-5 h-5 fill-current" /></button>
            <button onClick={() => shift(1)} className="pio-t" aria-label="Next visualizer" data-testid="pioneer-forward"><FastForward className="w-5 h-5 fill-current" /></button>
            <button onClick={() => audio.playing && audio.toggle()} className="pio-t" aria-label="Stop" data-testid="pioneer-stop"><Square className="w-4 h-4 fill-current" /></button>
            <button onClick={() => setMode(mode === "AUTO" ? "SPEC" : "AUTO")} className={`pio-t ${mode === "AUTO" ? "text-[#3ee07a]" : ""}`} aria-label="Auto cycle visualizers" data-testid="pioneer-auto"><Shuffle className="w-5 h-5" /></button>
            <button onClick={() => shift(1)} className="pio-t hidden sm:flex" aria-label="Cycle visualizer" data-testid="pioneer-repeat"><Repeat className="w-5 h-5" /></button>
            <span className="pio-model ml-auto whitespace-nowrap hidden sm:inline">LLR-G2026BT</span>
          </div>
        </div>
      </div>
    </section>
  );
};
